import { Controller } from '@hotwired/stimulus'
import flatpickr from 'flatpickr'
import BusyElementsController from './busy-elements'

interface DatepickerInput extends HTMLInputElement {
  datepicker: flatpickr.Instance
}

interface DisableableHTMLElement extends HTMLButtonElement {
  disabled: boolean
}

export default class BulkActionsController extends Controller<HTMLElement> {
  declare checkedValue: Boolean
  declare idsValue: Array<string>
  declare indeterminateValue: Boolean
  declare readonly actionFormTargets: HTMLFormElement[]
  declare readonly actionListTarget?: HTMLElement
  declare readonly actionInputTargets: HTMLInputElement[]
  declare readonly bulkCheckboxTarget?: HTMLInputElement
  declare readonly checkboxTargets: HTMLInputElement[]
  declare readonly hasBulkCheckboxTarget: boolean
  declare readonly hasActionListTarget: boolean
  declare readonly actionTargets: DisableableHTMLElement[]
  declare readonly actionContainerTargets: HTMLElement[]

  static targets = [
    'actionForm', // Each action requires its own form to submit the action.
    // Each form will have one `actionInput` per checked item.
    'actionList', // The container for actions that can be performed on the
    // checked items. Hidden until some checkboxes are checked.
    'actionInput', // The input that will be submitted within an individual
    // action's form to indicate which items are selected.
    'bulkCheckbox', // The checkbox that toggles all individual checkbox items
    'checkbox', // An individual checkbox
    'actionContainer', // The action element container
    'action', // Action leaf element which can be enabled/disabled
  ]

  static values = {
    checked: Boolean,
    ids: Array,
    indeterminate: Boolean,
  }

  connect() {
    // HACK: We need to check that this controller's targets exist before
    // proceeding, but I'm not sure why. In theory, all of the markup exists on
    // the page at the point of instantiation, so this should never be false.
    // However, Stimulus doesn't find the targets upon first connection.
    // This may be an issue caused by using Stimulus + Phlex, but I'm not sure.
    if (this.hasBulkCheckboxTarget) {
      this.#handleCheckboxChange()
    }
  }

  // HACK: This is a workaround for a bug in Stimulus that causes datepicker
  // inputs that live inside of the actionListTarget to have their position
  // calculated incorrectly. It's related to the issue described in the connect
  // method above.
  actionListTargetConnected(elem: HTMLElement) {
    const datepickers = elem.querySelectorAll<DatepickerInput>('[data-controller="datepicker"]')

    datepickers.forEach(elem => {
      elem.datepicker?.redraw()
    })
  }

  bulkCheckboxTargetConnected(elem: HTMLInputElement) {
    elem.addEventListener('input', this.#handleBulkCheckboxChange.bind(this))

    if (elem.form) {
      elem.form.addEventListener('reset', this.#handleCheckboxChange.bind(this))
    }
  }

  bulkCheckboxTargetDisconnected(elem: HTMLInputElement) {
    elem.removeEventListener('input', this.#handleBulkCheckboxChange.bind(this))

    if (elem.form) {
      elem.form.removeEventListener('reset', this.#handleCheckboxChange.bind(this))
    }
  }

  checkboxTargetConnected(elem: HTMLInputElement) {
    elem.addEventListener('input', this.#handleCheckboxChange.bind(this))
  }

  checkboxTargetDisconnected(elem: HTMLInputElement) {
    elem.removeEventListener('input', this.#handleCheckboxChange.bind(this))
  }

  idsValueChanged() {
    this.dispatch('idsValue:changed', { detail: { ids: this.idsValue } })
  }

  // Public interfaces
  checkboxChanged = this.#handleCheckboxChange.bind(this)
  toggleActionDisabledState = this.#handleDisabledToggleAction.bind(this)

  // Private methods

  #removeActionInputs() {
    this.actionInputTargets.forEach(elem => {
      elem.remove()
    })
  }

  #generateActionInput(form: HTMLFormElement, id: string) {
    const name = form.dataset.bulkActionsActionInputName || 'ids[]'
    const input = document.createElement('input')

    input.dataset.bulkActionsTarget = 'actionInput'
    input.name = name
    input.type = 'hidden'
    input.value = id

    return input
  }

  #handleBulkCheckboxChange(event: Event) {
    const { checked } = event.target as HTMLInputElement
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = checked
    })
    this.#setValues()
    this.#setBulkActionVisibility()
  }

  #handleCheckboxChange() {
    // NOTE: an immediately fired setTimeout is used to ensure that the
    // checkboxes are in the state that we expect them to be in.
    // This is only really needed in the case of the form `reset` event
    // (which may not even be present depending on where this bulk edit is used)
    setTimeout(() => {
      this.#setValues({ updateBulkCheckbox: true })
      this.#setBulkActionVisibility()
    }, 0)
  }

  #setBulkActionVisibility() {
    if (!this.hasActionListTarget) return

    if (this.checkedValue) {
      this.actionListTarget.removeAttribute('inert')
      this.actionListTarget.classList.remove('opacity-50')
    } else {
      this.actionListTarget.setAttribute('inert', '')
      this.actionListTarget.classList.add('opacity-50')
    }
  }

  #setValues({ updateBulkCheckbox = false } = {}) {
    const checkedBoxes = this.checkboxTargets.filter(elem => elem.checked)
    const checked = checkedBoxes.length > 0
    const indeterminate = checked && checkedBoxes.length < this.checkboxTargets.length

    this.checkedValue = checked
    this.indeterminateValue = indeterminate
    this.idsValue = checked ? checkedBoxes.map(elem => elem.value) : []

    if (updateBulkCheckbox && this.hasBulkCheckboxTarget) {
      this.bulkCheckboxTarget.checked = checked
      this.bulkCheckboxTarget.indeterminate = indeterminate
    }

    this.#updateForms()
  }

  #updateForms() {
    this.#removeActionInputs()

    this.actionFormTargets.forEach(form => {
      const newActionInputs = new DocumentFragment()

      this.idsValue.forEach(id => {
        newActionInputs.append(this.#generateActionInput(form, id))
      })

      form.append(newActionInputs)
    })
  }

  #handleDisabledToggleAction(
    name: string,
    {
      cascadeToBusy = true,
      forceValue = null,
      busyOptions = {},
    }: {
      cascadeToBusy?: boolean
      forceValue?: boolean
      busyOptions?: {
        updateTooltipContent?: null
        busyType?: 'indeterminate' | 'progress'
        progress?: number
      }
    } = {},
  ) {
    const container = this.actionContainerTargets.find(el => el.dataset.bulkActionName === name)
    if (!container) {
      console.warn(`Bulk action container with name ${name} not found. Skipping.`)
      return
    }
    let next: boolean
    const el = container.querySelector<DisableableHTMLElement>('[data-bulk-action-target=action]')
    if (!el) {
      console.warn(`Bulk action container with name ${name} does not contain a proper action. Skipping.`)
    }
    if (forceValue != null) {
      next = forceValue
    } else {
      next = !el.disabled
    }

    el.disabled = next
    if (cascadeToBusy) {
      this.#handleSetActionBusyState(name, next, busyOptions)
    }
  }

  #getActionElement(name) {
    return this.actionContainerTargets.find(el => el.dataset.bulkActionName === name)
  }

  #handleSetActionBusyState(
    name: string,
    next: boolean,
    {
      updateTooltipContent = null,
      busyType = 'indeterminate',
      progress = 0,
    }: {
      updateTooltipContent?: string
      busyType?: 'indeterminate' | 'progress'
      progress?: number
    } = {},
  ) {
    this.#setDisabledAllCheckboxes(next)
    const el = this.#getActionElement(name)
    if (!el) {
      console.warn(`Bulk action with name ${name} not found. Skipping.`)
    }
    const busyElementsCtrl = window.Stimulus.getControllerForElementAndIdentifier(
      el.querySelector("[data-controller*='busy-elements']"),
      'busy-elements',
    ) as BusyElementsController

    if (!busyElementsCtrl) {
      console.warn(`BulkActionsController: No BusyElementsController found for ${name}. Skipping`)
      return
    }

    if (next) {
      busyElementsCtrl.setState(busyType, { progressTo: progress, tooltipTo: updateTooltipContent })
    } else {
      busyElementsCtrl.setState('idle')
    }
  }

  #setDisabledAllCheckboxes(nextVal: boolean) {
    this.checkboxTargets.concat([this.bulkCheckboxTarget]).forEach(el => {
      el.disabled = nextVal
    })
  }
}
